import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { VerificationEvidencePreview } from "../verification-evidence-preview/verification-evidence-preview";
import { VerificationSourceLink } from "../verification-source-link";
import { DisciplinaryDetails } from "./disciplinary-details";
import { LicenseResult } from "./license-result";
import {
  disciplinaryEvidenceKeys,
  disciplinaryEvidenceLabel,
  isPublicBoardEvidence,
} from "./disciplinary-evidence";
import { useOpenVerificationEvidence } from "./use-open-verification-evidence";
import {
  EVIDENCE_KIND,
  getAssessmentItem,
  getLicenseAttachments,
  getLicenseEvidence,
  getResultAttachments,
  PDF_MEDIA_TYPE,
  PNG_MEDIA_TYPE,
  type Evidence,
} from "./result-issues";
import {
  CHECK_STATUS_LABELS,
  type ProviderReadScope,
  type VerificationCheck,
} from "./types";

/** Shown when a failed or blocked check has no recovery guidance of its own. */
export const NO_RESULT_ADVICE =
  "No verification result is available from this check. Confirm the license details and try again, or review the licensing board directly.";
export function VerificationCheckCard({
  check,
  runId,
  scope,
  embedded = false,
  resultKey,
  informational = false,
  showIssues = true,
}: {
  check: VerificationCheck;
  runId: string;
  scope: ProviderReadScope;
  embedded?: boolean;
  /** Shows only this result of a check that covers several licenses, without
   * the check's other captures. */
  resultKey?: string;
  /** Shows results as neutral information; see `LicenseResult`. */
  informational?: boolean;
  /** Hides the result's issues when the page already states them once. */
  showIssues?: boolean;
}) {
  const openEvidence = useOpenVerificationEvidence({
    scope,
    runId,
    checkId: check.id,
  });
  const shownResults = (check.results ?? []).filter(
    (result) => resultKey === undefined || result.resultKey === resultKey,
  );
  const results = shownResults.map((result) => ({
    result,
    evidence: getLicenseEvidence(check, result),
    assessment: getAssessmentItem(check, result),
  }));
  const primaryFileIds = new Set(
    results.flatMap(({ evidence }) =>
      evidence ? [evidence.fileUploadId] : [],
    ),
  );
  const publicBoardKeys = new Set(
    results.flatMap(({ assessment }) =>
      assessment?.disciplinary
        ? [...disciplinaryEvidenceKeys(assessment.disciplinary)]
        : [],
    ),
  );
  // Captures of a check shared by several licenses are not tied to one.
  const remainingCaptures =
    resultKey === undefined
      ? check.evidenceManifest.filter(
          (evidence) =>
            !primaryFileIds.has(evidence.fileUploadId) &&
            !publicBoardKeys.has(evidence.key),
        )
      : [];
  const otherBoardCaptures = remainingCaptures.filter(isPublicBoardEvidence);
  const otherCaptures = remainingCaptures.filter(
    (evidence) => !isPublicBoardEvidence(evidence),
  );

  return (
    <div className={cn("space-y-3", !embedded && "rounded-md border p-3")}>
      {!embedded && (
        <div>
          <h4 className="text-sm font-medium">{check.authorityName}</h4>
          <p className="text-xs text-muted-foreground">
            {CHECK_STATUS_LABELS[check.status]}
          </p>
        </div>
      )}
      {(check.status === "failed" || check.status === "blocked") && (
        <div className="space-y-2 text-xs text-muted-foreground">
          <p>{check.recovery?.description ?? NO_RESULT_ADVICE}</p>
          {check.recovery?.sourceLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="block underline"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
      {results.map(({ result, evidence, assessment }, index) => {
        const attachments = getLicenseAttachments(check, result);
        const sourceUrl =
          result.facts.sourceUrl ??
          getResultAttachments(check, result).find(
            (attachment) => attachment.sourceUrl,
          )?.sourceUrl;
        return (
          <div
            key={result.resultKey}
            className={cn(
              "space-y-4 text-xs",
              (!embedded || index > 0) && "border-t pt-4",
            )}
          >
            <LicenseResult
              informational={informational}
              showIssues={showIssues}
              result={result}
              assessment={assessment}
              attachments={attachments}
              evidenceActions={
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {evidence && (
                      <EvidenceButton
                        evidence={evidence}
                        label={licenseEvidenceLabel(evidence)}
                        onOpen={openEvidence.open}
                      />
                    )}
                    <VerificationSourceLink sourceUrl={sourceUrl} />
                  </div>
                  {!evidence && (
                    <p className="text-muted-foreground">
                      No validated license evidence is available for this
                      result.
                    </p>
                  )}
                </div>
              }
            />
            {assessment?.disciplinary && (
              <DisciplinaryDetails
                assessment={assessment.disciplinary}
                evidence={check.evidenceManifest}
                onOpenEvidence={openEvidence.open}
              />
            )}
          </div>
        );
      })}
      {otherBoardCaptures.length > 0 && (
        <section
          aria-label="Other public board evidence"
          className="space-y-2 border-t pt-2"
        >
          <h5 className="text-xs font-medium">Other public board evidence</h5>
          <div className="flex flex-wrap gap-2">
            {otherBoardCaptures.map((evidence, index) => (
              <EvidenceButton
                key={evidence.fileUploadId}
                evidence={evidence}
                label={getSupportingCaptureLabel(evidence, index)}
                onOpen={openEvidence.open}
              />
            ))}
          </div>
        </section>
      )}
      {otherCaptures.length > 0 && (
        <details className="space-y-2 border-t pt-2">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Diagnostics and other captures ({otherCaptures.length})
          </summary>
          <p className="text-xs text-muted-foreground">
            These supporting or diagnostic captures are not validated license
            evidence.
          </p>
          <div className="flex flex-wrap gap-2">
            {otherCaptures.map((evidence, index) => (
              <EvidenceButton
                key={evidence.fileUploadId}
                evidence={evidence}
                label={getSupportingCaptureLabel(evidence, index)}
                onOpen={openEvidence.open}
              />
            ))}
          </div>
        </details>
      )}
      <VerificationEvidencePreview {...openEvidence} />
    </div>
  );
}

function licenseEvidenceLabel(evidence: Evidence) {
  if (evidence.mediaType === PNG_MEDIA_TYPE) {
    return "License screenshot";
  }
  if (evidence.mediaType === PDF_MEDIA_TYPE) {
    return "License report (PDF)";
  }
  return "License source record";
}

function getSupportingCaptureLabel(evidence: Evidence, index: number) {
  if (isPublicBoardEvidence(evidence)) {
    return disciplinaryEvidenceLabel(evidence, index);
  }
  if (evidence.mediaType === PDF_MEDIA_TYPE) {
    return `Supporting report ${index + 1} (PDF)`;
  }
  if (evidence.mediaType === PNG_MEDIA_TYPE) {
    if (evidence.kind === EVIDENCE_KIND.Diagnostic) {
      return `Diagnostic screenshot ${index + 1}`;
    }
    if (evidence.kind === EVIDENCE_KIND.LicenseProfile) {
      return `Other license-page capture ${index + 1}`;
    }
    if (evidence.kind === EVIDENCE_KIND.SourceData) {
      return `Supporting screenshot ${index + 1}`;
    }
    return `Unclassified screenshot ${index + 1}`;
  }
  if (evidence.kind === EVIDENCE_KIND.SourceData) {
    return `Source data ${index + 1}`;
  }
  return `Supporting capture ${index + 1}`;
}

function EvidenceButton({
  evidence,
  label,
  onOpen,
}: {
  evidence: Evidence;
  label: string;
  onOpen: (target: { fileUploadId: string; label: string }) => void;
}) {
  return (
    <Button
      type="button"
      variant="surface"
      size="sm"
      className="gap-1.5"
      onClick={() => onOpen({ fileUploadId: evidence.fileUploadId, label })}
    >
      <Eye className="size-4" aria-hidden />
      {label}
    </Button>
  );
}
